import { useEffect } from "react";
import { connectControl } from "../../lib/ws";
import { useViewStore } from "../store/viewStore";
import { type ViewPusher, createViewPusher } from "../lib/viewPusher";

/**
 * Keeps every screen showing the edited view in step with the editor.
 *
 * One socket for the lifetime of the editor. The store's own `subscribe` is the edit
 * signal — every mutation replaces `view` and moves `rev`, and the pusher's generation
 * check turns the sets that changed nothing worth sending (a save echo, a status flag)
 * back into no-ops.
 *
 * Nothing here keeps a temporary view alive: a control socket joins no view room, so the
 * server never counts it as a reader. That is the screen's ws ping and the editor's own
 * HTTP beat (`viewKeepAlive`), and duplicating it here would only hide a failure in
 * either.
 *
 * Returns nothing on purpose. A connection indicator is chrome, and the editor's header
 * is specified in the views-and-devices UI spec; adding one here would be deciding that
 * screen's layout from the data layer.
 */
export function useViewConn(): void {
  useEffect(() => {
    // The two reference each other, so one of them has to be assignable. `onStatus` can
    // only fire from `ws.onopen`, which is after this body returns, so the null is never
    // actually observed — it is here so neither name is a forward reference to a const.
    let pusher: ViewPusher | null = null;
    const conn = connectControl({
      onStatus: (connected) => {
        // A reconnected controller has to re-state what it is editing: the screens got
        // their `config` from the last SAVED state, and an unpersisted edit has only ever
        // existed on the wire.
        if (connected) pusher?.flushNow();
      },
    });
    // `conn` is the target as-is: ControlConn's `pushUpdate(view: View)` is exactly
    // ViewPusherTarget, so there is no adapter to get wrong.
    pusher = createViewPusher(conn, () => {
      const s = useViewStore.getState();
      return { view: s.view, rev: s.rev };
    });
    const unsubscribe = useViewStore.subscribe(() => pusher?.schedule());
    return () => {
      unsubscribe();
      pusher?.cancel();
      conn.close();
    };
  }, []);
}
